import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "./ClientApprovalWizard.module.css";
import WizardPageController from "../../reusable_components/WizardPageController/WizardPageController";
import { Col, Modal, Row } from "react-bootstrap";
import { WizardPageConfig } from "../../reusable_components/WizardPageController/WizardPageController.utils";
import WizardHeaderPagination from "../../reusable_components/WizardHeaderPagination/WizardHeaderPagination";
import {
  clientApprovalComponentMap,
  ClientApprovalWizardProps,
  ClientApprovalWizardSharedData,
  COI_CONFLICT_REVIEW_LABEL,
  FileInputTypes,
  hasUsableId,
  toNumericId,
} from "./ClientApprovalWizard.utils";
import { FormRecord } from "../ClientCreator/ClientCreator.utils";
import {
  approveRecord,
  fetchDocumentTypes,
  fetchFormData,
} from "./ClientApprovalWizard.service";
import { fetchClientData } from "../ClientEditor/ClientEditor.service";
import * as Icon from "react-bootstrap-icons";
import { ApprovalMessageModal } from "../ApprovalMessageModal";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { CustomToaster } from "../../reusable_components/Toasters/toaster";
import {
  COI_RESULTS_STEP,
  FlowStep,
  getFormFlowByPurpose,
} from "../ClientsGrid/ClientsGrid.utils";
import { COI_REVIEW_PURPOSE } from "../../reusable_components/WizardHeaderPagination/WizardHeaderPagination.utils";

// Helpers
const cfgSig = (cfg: { key: string; show?: boolean; order?: number }[]) =>
  cfg.map((s) => `${s.key}:${s.show ? 1 : 0}:${s.order ?? ""}`).join("|");

// Helper function to check if a form ID is valid
const isValidFormId = (id: any): boolean => {
  return id !== null &&
    id !== undefined &&
    id !== 'N/A' &&
    id !== '' &&
    String(id) !== 'N/A';
};

// Map config idField to the actual prop/sharedData key
const ID_FIELD_TO_PROP: Record<string, string> = {
  AttestFormId: "attestFormId",
  OnboardingFormId: "onboardingFormId",
  TaxAcceptanceFormId: "taxAcceptanceFormId",
  AuditAcceptanceFormId: "auditAcceptanceFormId",
  DocumentsUploadId: "documentsUploadId", // Documents page is always shown if in config
  COIApprovalStatus: "coiApprovalStatus",
};

const ClientApprovalWizard: React.FC<ClientApprovalWizardProps> = ({
  show,
  onHide,
  clientMasterId,
  processedTemplateId,
  transporter,
  coiReviewMode,
  coiMatches,
  coiCanDecide,
  coiMatchesLoading,
  attestFormId,
  onboardingFormId,
  taxAcceptanceFormId,
  auditAcceptanceFormId,
  firmDetailId,
  clientRecord,
  rejectOptions,
  refresh,
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const [coiActions, setCoiActions] = useState<{
    allDecided: boolean;
    saving: "draft" | "submit" | null;
    submit: (asDraft: boolean) => void;
  } | null>(null);

  // Determine purpose from clientRecord
  const purpose = useMemo(() => {
    if (clientRecord?.BusinessUnit) return clientRecord.BusinessUnit.toLowerCase();
    return "tax";
  }, [clientRecord]);

  // Get flow configuration based on purpose. COI_RESULTS is appended here,
  // local to the approval wizard only -- it isn't part of FLOW_BY_PURPOSE
  // since ClientEditor/ClientCreator share that same config for their own
  // tab sets and have no renderer for this key.
  const flowConfig = useMemo(() => {
    const config = getFormFlowByPurpose(purpose as any);
    const base = config.length > 0 ? config : getFormFlowByPurpose("tax" as any);
    const fullFlow = base
      .map((step) => (step.key === "DOCUMENTS" ? { ...step, order: step.order + 1 } : step))
      .concat(COI_RESULTS_STEP);

    if (!coiReviewMode) return fullFlow;

    const onboardingStep = fullFlow.find((step) => step.key === "CLIENT_ONBOARDING");
    return [
      ...(onboardingStep ? [{ ...onboardingStep, order: 0 }] : []),
      { ...COI_RESULTS_STEP, label: COI_CONFLICT_REVIEW_LABEL, order: 1 },
    ];
  }, [purpose, coiReviewMode]);

  // Build form ID lookup from props
  const formIdLookup = useMemo(() => ({
    attestFormId,
    onboardingFormId,
    taxAcceptanceFormId,
    auditAcceptanceFormId,
    documentsUploadId: true, // Documents is always available if in config
    coiApprovalStatus: clientRecord?.COIApprovalStatus,
  }), [attestFormId, onboardingFormId, taxAcceptanceFormId, auditAcceptanceFormId, clientRecord]);

  // Convert flow config to wizard pages
  const flowToWizardConfig = useCallback((
    flow: FlowStep[],
    idLookup: Record<string, any>
  ): WizardPageConfig[] => {
    return flow
      .filter((step) => step.enabled)
      .sort((a, b) => a.order - b.order)
      .map((step) => {
        // Get the prop key for this step's form ID
        const propKey = ID_FIELD_TO_PROP[step.idField] || step.idField;
        const formId = idLookup[propKey];
        
        // Documents page is always shown if enabled in config
        const isDocuments = step.key === "DOCUMENTS";
        const isCoiReviewStep = coiReviewMode && (step.key === "CLIENT_ONBOARDING" || step.key === "COI_RESULTS");
        const shouldShow = isDocuments || isCoiReviewStep ? true : isValidFormId(formId);

        // Map config key to component key if needed
        const componentKey = step.key;

        return {
          key: componentKey as WizardPageConfig["key"],
          label: step.label,
          show: shouldShow,
          order: step.order,
          validate: false,
          props: step.key === "COI_RESULTS" ? { onActionsChange: setCoiActions } : undefined,
        };
      });
  }, [coiReviewMode]);

  const [approvalWizardConfig, setApprovalWizardConfig] = useState<WizardPageConfig[]>(() => {
    return flowToWizardConfig(flowConfig, formIdLookup);
  });

  // Update config when flow or form IDs change
  useEffect(() => {
    const newConfig = flowToWizardConfig(flowConfig, formIdLookup);
    if (cfgSig(newConfig) !== cfgSig(approvalWizardConfig)) {
      setApprovalWizardConfig(newConfig);
      const visible = newConfig
        .filter((p) => p.show)
        .sort((a, b) => a.order - b.order);
      const maxIdx = Math.max(0, visible.length - 1);
      if (activeStep > maxIdx) setActiveStep(maxIdx);
    }
  }, [flowConfig, formIdLookup, flowToWizardConfig, approvalWizardConfig, activeStep]);

  // Determine which form types are in the current config (for loading state tracking)
  const configuredFormTypes = useMemo(() => {
    const types = {
      attest: false,
      onboarding: false,
      acceptance: false,
    };
    
    flowConfig.forEach((step) => {
      if (step.key === "CLIENT_ATTEST") types.attest = true;
      if (step.key === "CLIENT_ONBOARDING") types.onboarding = true;
      if (step.key === "CLIENT_TAX_ACCEPTANCE" || step.key === "CLIENT_AUDIT_ACCEPTANCE") {
        types.acceptance = true;
      }
    });
    
    return types;
  }, [flowConfig]);

  // Add loading state tracker for each form
  const [formsLoadingState, setFormsLoadingState] = useState<{
    attest: boolean;
    onboarding: boolean;
    acceptance: boolean;
  }>({
    attest: false,
    onboarding: false,
    acceptance: false,
  });

  const allFormsLoaded = useMemo(() => {
    return !formsLoadingState.attest &&
      !formsLoadingState.onboarding &&
      !formsLoadingState.acceptance;
  }, [formsLoadingState]);

  // Callback for form viewers to report they're loaded
  const handleFormLoaded = useCallback((formType: 'attest' | 'onboarding' | 'acceptance') => {
    console.log(`Form loaded callback received: ${formType}`);
    setFormsLoadingState(prev => ({
      ...prev,
      [formType]: false
    }));
  }, []);

  // Reset loading states when wizard opens or form IDs change
  useEffect(() => {
    if (show) {
      const newState = {
        attest: configuredFormTypes.attest && isValidFormId(attestFormId),
        onboarding: configuredFormTypes.onboarding && isValidFormId(onboardingFormId),
        acceptance: configuredFormTypes.acceptance && 
          (isValidFormId(taxAcceptanceFormId) || isValidFormId(auditAcceptanceFormId)),
      };

      setFormsLoadingState(newState);
    }
  }, [show, attestFormId, onboardingFormId, taxAcceptanceFormId, auditAcceptanceFormId, configuredFormTypes]);

  // Mark forms as loaded if they're not shown in the config
  useEffect(() => {
    if (!show) return;
    
    const visiblePages = approvalWizardConfig.filter(p => p.show);
    const hasAttestPage = visiblePages.some(p => p.key === 'CLIENT_ATTEST');
    const hasOnboardingPage = visiblePages.some(p => p.key === 'CLIENT_ONBOARDING');
    const hasAcceptancePage = visiblePages.some(p =>
      p.key === 'CLIENT_TAX_ACCEPTANCE' || p.key === 'CLIENT_AUDIT_ACCEPTANCE'
    );

    // console.log('  Visible pages check:');
    // console.log('  Attest visible:', hasAttestPage);
    // console.log('  Onboarding visible:', hasOnboardingPage);
    // console.log('  Acceptance visible:', hasAcceptancePage);

    // If a form page isn't visible, mark it as loaded (not loading)
    setFormsLoadingState(prev => ({
      attest: hasAttestPage ? prev.attest : false,
      onboarding: hasOnboardingPage ? prev.onboarding : false,
      acceptance: hasAcceptancePage ? prev.acceptance : false,
    }));
  }, [show, approvalWizardConfig]);

  const [sharedData, setSharedData] = useState<ClientApprovalWizardSharedData>({
    clientMasterId,
    attestFormId,
    onboardingFormId,
    taxAcceptanceFormId,
    auditAcceptanceFormId,
    firmDetailId,
    attestFormData: [],
    onboardingFormData: [],
    acceptanceFormData: [],
    isPublic: false,
    selectedPurposes: [],
    fileInputs: [],
    onboardingFeedback: "",
    acceptanceFeedback: "",
    backgroundFeedback: "",
    validations: {},
    approvalAPIProgress: false,
  });

  const [attestFormDetails, setAttestFormDetails] = useState<FormRecord>({
    FirmFormId: 0,
    FirmId: 1,
    FirmFormsTemplateId: 1,
    FormName: "",
    FormDescription: "",
    FormPurpose: "",
    InputStructure: { fields: [], columns: {} },
    Url: "",
    FormTypeCode: "",
    ClientTypeCode: "",
    ClientTypeName: "",
    CreatedDate: "",
    UpdatedDate: "",
    CreatedBy: "",
    UpdatedBy: "",
  });

  const [onboardingFormDetails, setOnboardingFormDetails] = useState<FormRecord>({
    FirmFormId: 0,
    FirmId: 1,
    FirmFormsTemplateId: 1,
    FormName: "",
    FormDescription: "",
    FormPurpose: "",
    InputStructure: { fields: [], columns: {} },
    Url: "",
    FormTypeCode: "",
    ClientTypeCode: "",
    ClientTypeName: "",
    CreatedDate: "",
    UpdatedDate: "",
    CreatedBy: "",
    UpdatedBy: "",
  });

  const [acceptanceFormDetails, setAcceptanceFormDetails] = useState<FormRecord>({
    FirmFormId: 0,
    FirmId: 1,
    FirmFormsTemplateId: 1,
    FormName: "",
    FormDescription: "",
    FormPurpose: "",
    FormTypeCode: "",
    InputStructure: { fields: [], columns: {} },
    Url: "",
    ClientTypeCode: "",
    ClientTypeName: "",
    CreatedDate: "",
    UpdatedDate: "",
    CreatedBy: "",
    UpdatedBy: "",
  });

  const [clientDetails, setClientDetails] = useState<any>();
  const [approvalMessageModal, setApprovalMessageModal] = useState(false);
  const [formApiProgress, setFormAPiProgress] = useState<boolean>(false);

  // View mode
  const [viewMode, setViewMode] = useState<"wizard" | "scrollable">("wizard");
  const [pendingViewMode, setPendingViewMode] = useState<"wizard" | "scrollable">("wizard");
  const [animateViewSwitch, setAnimateViewSwitch] = useState(false);
  const animationOrigin = { x: 0, y: 0 };
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);

  // Approval status
  const prevStatusRef = useRef<string | undefined>(undefined);
  const [pendingDecision, setPendingDecision] = useState<
    "Approved" | "Rejected" | null
  >(null);
  
  const isCoiApproverRow = clientRecord?.COIApprover === "Y" || Boolean(coiReviewMode);

  const openDecision = (status: "Approved" | "Rejected") => {
    if (isCoiApproverRow) {
      CustomToaster({
        type: "error",
        customized_message: "This approval must be resolved via the Conflict Check review screen.",
        api_message: "",
      });
      return;
    }

    if (!allFormsLoaded && status === "Approved") {
      console.warn("Cannot approve: Forms are still loading");
      return;
    }

    prevStatusRef.current = sharedData.Status;
    const updatedSharedData = { ...sharedData, Status: status };
    setSharedData(updatedSharedData);

    if (status === "Rejected") {
      setApprovalMessageModal(true);
    } else {
      // Directly call approval with empty message
      approveRecord("", updatedSharedData, setSharedData, onHide, refresh);
    }
  };

  // Files
  const [fileInputs, setFileInputs] = useState<FileInputTypes[]>([]);

  // Populate sharedData.clientMasterId
  useEffect(() => {
    setSharedData((prev) => ({
      ...prev,
      clientMasterId,
      processedTemplateId,
      transporter,
      coiReviewMode,
      coiMatches,
      coiCanDecide,
      coiMatchesLoading,
      attestFormId,
      onboardingFormId,
      taxAcceptanceFormId,
      auditAcceptanceFormId,
      firmDetailId,
    }));
  }, [
    clientMasterId,
    processedTemplateId,
    transporter,
    coiReviewMode,
    coiMatches,
    coiCanDecide,
    coiMatchesLoading,
    attestFormId,
    onboardingFormId,
    taxAcceptanceFormId,
    auditAcceptanceFormId,
    firmDetailId,
  ]);

  useEffect(() => {
    setSharedData((prev) => ({ ...prev, purpose }));
  }, [purpose]);

  // Map fetched form details → read-only fields filled from clientDetails
  useEffect(() => {
    if (attestFormDetails?.InputStructure?.fields?.length && clientDetails) {
      const newComponents = [...attestFormDetails.InputStructure.fields].map(
        (field) => {
          return {
            ...field,
            value:
              (field as any).allfields_code
                ? clientDetails[(field as any).allfields_code] ?? field.value
                : field.value,
            disabled: true,
          };
        }
      );
      setSharedData((prev) => ({ ...prev, attestFormData: newComponents, attestFieldVariant: attestFormDetails.InputStructure.formSettings?.fieldVariant, attestSectionVariant: attestFormDetails.InputStructure.formSettings?.sectionVariant, attestSectionLayout: attestFormDetails.InputStructure.formSettings?.sectionLayout }));
    }
  }, [attestFormDetails, clientDetails]);

  useEffect(() => {
    if (
      onboardingFormDetails?.InputStructure?.fields?.length &&
      clientDetails
    ) {
      const newComponents = [
        ...onboardingFormDetails.InputStructure.fields,
      ].map((field) => {
        return {
          ...field,
          value:
            (field as any).allfields_code
              ? clientDetails[(field as any).allfields_code] ?? field.value
              : field.value,
          disabled: true,
        };
      });
      setSharedData((prev) => ({ ...prev, onboardingFormData: newComponents, onboardingFieldVariant: onboardingFormDetails.InputStructure.formSettings?.fieldVariant, onboardingSectionVariant: onboardingFormDetails.InputStructure.formSettings?.sectionVariant, onboardingSectionLayout: onboardingFormDetails.InputStructure.formSettings?.sectionLayout, onboardingIntakeRules: onboardingFormDetails.InputStructure.formSettings?.intakeRules }));
    }
  }, [onboardingFormDetails, clientDetails]);

  useEffect(() => {
    if (
      acceptanceFormDetails?.InputStructure?.fields?.length &&
      clientDetails
    ) {
      const newComponents = [
        ...acceptanceFormDetails.InputStructure.fields,
      ].map((field) => {
        return {
          ...field,
          value:
            (field as any).allfields_code
              ? clientDetails[(field as any).allfields_code] ?? field.value
              : field.value,
          disabled: true,
        };
      });
      setSharedData((prev) => ({ ...prev, acceptanceFormData: newComponents, acceptanceFieldVariant: acceptanceFormDetails.InputStructure.formSettings?.fieldVariant, acceptanceSectionVariant: acceptanceFormDetails.InputStructure.formSettings?.sectionVariant, acceptanceSectionLayout: acceptanceFormDetails.InputStructure.formSettings?.sectionLayout }));
    }
  }, [acceptanceFormDetails, clientDetails]);

  // Fetch data on open / when ids change
  useEffect(() => {
    const {
      attestFormId,
      onboardingFormId,
      taxAcceptanceFormId,
      auditAcceptanceFormId,
      clientMasterId,
    } = sharedData;

    if (hasUsableId(clientMasterId)) {
      fetchClientData(clientMasterId, setClientDetails, setFormAPiProgress);
    }

    // Only fetch forms that are configured for this purpose
    if (configuredFormTypes.attest) {
      const aId = toNumericId(attestFormId);
      if (aId !== undefined) fetchFormData(aId, setAttestFormDetails);
    }

    if (configuredFormTypes.onboarding) {
      const oId = toNumericId(onboardingFormId);
      if (oId !== undefined) fetchFormData(oId, setOnboardingFormDetails);
    }

    if (configuredFormTypes.acceptance) {
      const tacId = toNumericId(taxAcceptanceFormId);
      if (tacId !== undefined) fetchFormData(tacId, setAcceptanceFormDetails);

      const aacId = toNumericId(auditAcceptanceFormId);
      if (aacId !== undefined) fetchFormData(aacId, setAcceptanceFormDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sharedData.attestFormId,
    sharedData.onboardingFormId,
    sharedData.taxAcceptanceFormId,
    sharedData.auditAcceptanceFormId,
    sharedData.clientMasterId,
    configuredFormTypes,
  ]);

  // Document types
  useEffect(() => {
    fetchDocumentTypes(setFileInputs);
  }, []);

  useEffect(() => {
    if (fileInputs) setSharedData((prev) => ({ ...prev, fileInputs }));
  }, [fileInputs]);

  // View toggle (animation)
  const handleToggleView = () => {
    const target = viewMode === "wizard" ? "scrollable" : "wizard";
    setPendingViewMode(target);
    setAnimateViewSwitch(true);
  };

  useEffect(() => {
    if (!animateViewSwitch) return;
    const t1 = setTimeout(() => setViewMode(pendingViewMode), 750);
    const t2 = setTimeout(() => setAnimateViewSwitch(false), 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [animateViewSwitch, pendingViewMode]);

  const approvalMessageInput = (message: string) => {
    if (sharedData.Status !== undefined) {
      approveRecord(message, sharedData, setSharedData, onHide, refresh);
    }
  };

  return (
    <>
      {animateViewSwitch && (
        <>
          <div
            ref={overlayRef}
            className={styles.expandCircleOverlay}
            style={{ left: animationOrigin.x, top: animationOrigin.y }}
          />
          <div className={styles.overlayContent}>
            <h1>Switching to {pendingViewMode} view!</h1>
          </div>
        </>
      )}

      <ApprovalMessageModal
        show={approvalMessageModal}
        rejectOptions={rejectOptions}
        onHide={() => {
          setApprovalMessageModal(false);
          setSharedData((prev) => ({ ...prev, Status: prevStatusRef.current }));
        }}
        approvalcontinuation={(message: string) => {
          setApprovalMessageModal(false);
          approvalMessageInput(message);
        }}
      />

      <Modal
        show={show}
        onHide={() => onHide()}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        backdropClassName="custom-backdrop"
        className={styles.approvalWizardModal}
        centered
        scrollable={false}
        keyboard={false}
      >
        <Modal.Header
          className={
            viewMode === "scrollable"
              ? styles.approvalWizardScrollableHeader
              : styles.approvalWizardHeader
          }
          closeButton
        >
          {/* <Icon.Download
            title={pdfReady ? "Download" : "Loading PDF..."}
            onClick={pdfReady && !isGeneratingPdf ? handleDownloadApprovalPdf : undefined}
            className={styles.downloadPdfIcon}
            style={{
              opacity: pdfReady && !isGeneratingPdf ? 1 : 0.5,
              cursor: pdfReady && !isGeneratingPdf ? "pointer" : "not-allowed",
            }}
          /> */}

          {/* View toggle buttons */}
          {viewMode === "wizard" ? (
            <Icon.Stack
              className={styles.approvalWizardToggleButton}
              onClick={handleToggleView}
            />
          ) : (
            <Icon.TvFill
              className={styles.approvalWizardToggleButton}
              onClick={handleToggleView}
            />
          )}

          {viewMode === "wizard" && (
            <WizardHeaderPagination
              config={approvalWizardConfig}
              purpose={coiReviewMode ? COI_REVIEW_PURPOSE : "Client Approval"}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              sharedData={sharedData}
              setSharedData={setSharedData}
              onComplete={(sd, status) => {
                setSharedData((prev) => ({ ...prev, ...sd }));
                if (status === "Rejected") {
                  openDecision("Rejected");
                } else if (status === "Approved") {
                  openDecision("Approved");
                } else {
                  approvalMessageInput("");
                }
              }}
              coiSaving={coiActions?.saving ?? null}
              coiAllDecided={coiActions?.allDecided ?? false}
              onCoiSaveDraft={() => coiActions?.submit(true)}
              onCoiSubmit={() => coiActions?.submit(false)}
            />
          )}

          {viewMode === "scrollable" && (
            <div className={styles.buttonGroup}>
              <div className={styles.losContainer}>
                <h5 className={`${styles.losBadge}`}>
                  <span className={styles.label}>Line of service:</span>
                  <span className={styles.value}>
                    {purpose.toLocaleUpperCase()}
                  </span>
                </h5>
              </div>
              <CustomButton
                content={"Approve"}
                onclick={() => openDecision("Approved")}
                disabled={isCoiApproverRow || !allFormsLoaded || sharedData.approvalAPIProgress}
              />
              <CustomButton
                content={"Reject"}
                onclick={() => openDecision("Rejected")}
                disabled={isCoiApproverRow || sharedData.approvalAPIProgress}
              />
            </div>
          )}
        </Modal.Header>

        <Modal.Body ref={scrollableContentRef}>
          <Row>
            <Col md={12}>
              {viewMode === "wizard" ? (
                <WizardPageController<ClientApprovalWizardSharedData>
                  config={approvalWizardConfig}
                  componentMap={clientApprovalComponentMap}
                  sharedData={sharedData}
                  setSharedData={setSharedData}
                  activeStep={activeStep}
                  onFormLoaded={handleFormLoaded}
                />
              ) : (
                <>
                  {approvalWizardConfig
                    .filter((page) => page.show)
                    .sort((a, b) => a.order - b.order)
                    .map((page) => {
                      const Component = clientApprovalComponentMap[page.key];
                      return Component ? (
                        <div key={page.key} style={{ marginBottom: "2rem" }}>
                          <h5>{page.label}</h5>
                          <Component
                            sharedData={sharedData}
                            setSharedData={setSharedData}
                            onValidationChange={() => { }}
                            onFormLoaded={handleFormLoaded}
                          />
                        </div>
                      ) : null;
                    })}
                </>
              )}
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ClientApprovalWizard;
